import { FC, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Button from '@components/common/button';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { IModalComponentProps } from '@context/modal_context';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType,
    TTransaction
} from '@xeppt/xeppt-sdk/types';
import { currencies } from '@const/currency';
import {
    copyStringToClipboard,
    formatDate,
    getFirstLettersBySpace,
    getTransactionStatus,
    prettifyAmount
} from '@utils/index';
import { enumRecipientTranslate, ETransactionStatusTranslate } from '@locales/index';
import { useTranslation } from 'react-i18next';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiAccountService, apiTransactionsService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useUserContext } from '@hooks/context/useUserContext';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';
import Info from '@components/common/info';
import { getInternalTransferAction } from '@utils/payment';
import { EFundsOperation, ENewNotificationType } from '@enum';
import { useNotificationsContext } from '@hooks/context/useNotificationsContext';
import Typography from '@components/common/typography';

const fullInfoStatuses = [
    ETransactionStatus.PENDING,
    ETransactionStatus.APPROVED,
    ETransactionStatus.DECLINED
];

const ViewTransactionModal: FC<
    IModalComponentProps<{ transaction: TTransaction; refetchTransactions?: () => void }>
> = ({ onClose, modalData }) => {
    const { t, i18n } = useTranslation('transaction_details');
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();
    const { refetchAccount } = useUserContext();
    const { handleOpenSubmitRequestModal } = useHelpCenterModals();
    const { triggerNotificationType } = useNotificationsContext();
    const [reminderTimer, setReminderTimer] = useState(0);
    const [isListOpen, setIsListOpen] = useState(false);

    if (!modalData?.transaction) return null;

    const { transaction, refetchTransactions } = modalData;

    const isTransactionRequest = useMemo(
        () =>
            transaction?.action === ETransactionAction.REQUEST &&
            transaction?.status === ETransactionStatus.PENDING,
        [transaction]
    );

    const isCreditRequest = useMemo(() => {
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        return internalAction === EFundsOperation.WALLET_CREDIT_REQUEST;
    }, [transaction]);

    const isDebitRequest = useMemo(() => {
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        return internalAction === EFundsOperation.WALLET_DEBIT_REQUEST;
    }, [transaction]);

    const isInternalTransactionRequest = useMemo(
        () => isTransactionRequest && transaction?.method === ETransactionMethod.WALLET,
        [transaction]
    );

    const isNotMessage = useMemo(() => {
        if (transaction) {
            const actions = [
                ETransactionAction.CARD_SERVICE,
                ETransactionAction.CARD_DEPOSIT,
                ETransactionAction.PAYMENT
            ];
            const methods = [ETransactionMethod.EFT];
            return actions.includes(transaction?.action) || methods.includes(transaction.method);
        } else return false;
    }, [transaction]);

    const getRecipientName = useMemo(() => {
        if (transaction.type === ETransactionType.CREDIT) {
            return transaction.destination.split('(')?.[0] || '';
        } else {
            return transaction.source.split('(')?.[0] || '';
        }
    }, [transaction]);

    const getAvatar = useMemo(() => {
        if (
            transaction.method === ETransactionMethod.WALLET ||
            transaction.method === ETransactionMethod.E_TRANSFER
        ) {
            if (transaction.action === ETransactionAction.CARD_DEPOSIT) {
                return <Icon name="credit_card" width={24} height={24} />;
            }
            return getFirstLettersBySpace(getRecipientName?.split(' ')?.slice(0, 2).join(' '));
        } else if (ETransactionMethod.EFT === transaction.method) {
            return <Icon name="bank" width={24} height={24} />;
        } else if (ETransactionMethod.EPS === transaction.method) {
            return <Icon name="credit_card" width={24} height={24} />;
        } else if (ETransactionMethod.PAY_BILL === transaction.method) {
            return <Icon name="pay_bill" width={24} height={24} />;
        } else if (ETransactionMethod.CARD === transaction.method) {
            return <Icon name="credit_card" width={24} height={24} />;
        } else if (transaction.method === ETransactionMethod.GIFT_CARD) {
            return <Icon name="gift_card" width={24} height={24} />;
        }
        return '';
    }, [transaction, getRecipientName]);

    const getEmail = useMemo(() => {
        if (transaction.type === ETransactionType.CREDIT) {
            return transaction.destination.split('(')?.[1]?.replace(')', '') || '';
        } else {
            return transaction.source.split('(')?.[1]?.replace(')', '') || '';
        }
    }, [transaction]);

    const { handleRequest: handleApproveTransaction, isLoading: isApproveLoading } = useApiMutation(
        {
            method: () =>
                apiTransactionsService.approveRequestMoneyWallet(transaction.id as string),
            onSuccess: () => {
                if (refetchTransactions) {
                    refetchTransactions();
                } else {
                    triggerNotificationType(ENewNotificationType.TRANSACTION);
                }
                requestSuccessLocale('approve_transaction');
                refetchAccount();
                onClose();
            },
            onError: requestErrorLocale
        }
    );

    const { handleRequest: handleDeclineTransaction, isLoading: isDeclineLoading } = useApiMutation(
        {
            method: () =>
                apiTransactionsService.declineRequestMoneyWallet(transaction.id as string, ''),
            onSuccess: () => {
                if (refetchTransactions) {
                    refetchTransactions();
                } else {
                    triggerNotificationType(ENewNotificationType.TRANSACTION);
                }
                requestSuccessLocale('cancel_transaction');
                refetchAccount();
                onClose();
            },
            onError: requestErrorLocale
        }
    );

    const { handleRequest: handleCancelTransaction, isLoading: isCancelLoading } = useApiMutation({
        method: () => apiTransactionsService.cancelRequestMoneyWallet(transaction.id as string),
        onSuccess: () => {
            if (refetchTransactions) {
                refetchTransactions();
            } else {
                triggerNotificationType(ENewNotificationType.TRANSACTION);
            }
            requestSuccessLocale('cancel_transaction');
            refetchAccount();
            onClose();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleResendNotification, isLoading: isResendLoading } = useApiMutation({
        method: () =>
            apiTransactionsService.resendRequestMoneyNotification(transaction.id as string),
        onSuccess: () => {
            requestSuccessLocale('resend_transaction');
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleBlockContact } = useApiMutation({
        method: (val: string) => {
            return apiAccountService.blockMoneyRequester(val);
        },
        onSuccess: () => {
            requestSuccessLocale('block_contact');
            onClose();
        },
        onError: requestErrorLocale
    });

    const handleSendReminder = () => {
        handleResendNotification(undefined);
        setReminderTimer(60);
        const interval = setInterval(() => {
            setReminderTimer((state) => {
                if (state === 0) {
                    clearInterval(interval);
                    return 0;
                } else {
                    return state - 1;
                }
            });
        }, 1000);
    };

    const getTransactionName = useMemo(() => {
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        switch (internalAction) {
            case EFundsOperation.CARD_DEBIT_DEPOSIT:
                return 'Load balance';
            case EFundsOperation.CARD_CREDIT_WITHDRAWAL:
                return 'Payment with card';
            case EFundsOperation.E_TRANSFER_DEBIT_TRANSFER:
                return 'Receive money via e-Transfer';
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return 'Send money via e-Transfer';
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return 'Pay bill';
            case EFundsOperation.WALLET_CREDIT_CARD_SERVICE:
                return 'Order credit card';
            case EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT:
                return 'Load XEPPT card balance';
            case EFundsOperation.WALLET_CREDIT_REQUEST:
                return 'Receive request via XEPPT';
            case EFundsOperation.WALLET_DEBIT_TRANSFER:
                return 'Receive money via XEPPT';
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return 'Send money via XEPPT';
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return 'Send request via XEPPT';
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return 'Send request via e-Transfer';
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return 'Send money to bank account';
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return 'Load balance';
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return 'Load balance';
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return (transaction?.details?.gift_card_transaction_fee?.length || 0) > 1
                    ? 'Bulk XEPPT gift card issuance'
                    : 'XEPPT gift card issuance';
            case EFundsOperation.GIFT_CARD_CREDIT_RESERVE:
                return (transaction?.details?.gift_card_transaction_fee?.length || 0) > 1
                    ? 'Bulk XEPPT gift card funds reservation'
                    : 'XEPPT gift card funds reservation';
            case EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM:
                return 'XEPPT gift card redemption';
            case EFundsOperation.GIFT_CARD_DEBIT_RESERVE:
                return 'Return reserved funds';
        }
    }, [transaction]);

    const getSenderString = useMemo(() => {
        if (transaction.action === ETransactionAction.REFUND) {
            return {
                neutral: 'you refund',
                past: 'you refunded',
                amount: transaction?.amount
            };
        }
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        switch (internalAction) {
            case EFundsOperation.CARD_DEBIT_DEPOSIT:
                return {
                    neutral: '',
                    past: '',
                    amount: transaction?.amount
                };
            case EFundsOperation.CARD_CREDIT_WITHDRAWAL:
                return {
                    neutral: '',
                    past: '',
                    amount: transaction?.amount
                };
            case EFundsOperation.E_TRANSFER_DEBIT_TRANSFER:
                return {
                    neutral: 'you receive',
                    past: 'you received',
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_CREDIT_CARD_SERVICE:
                return {
                    neutral: 'you charge',
                    past: 'you charged',
                    amount: transaction?.amount + transaction?.fee
                };
            case EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.WALLET_CREDIT_REQUEST:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.WALLET_DEBIT_TRANSFER:
                return {
                    neutral: `you receive`,
                    past: `you received`,
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return {
                    neutral: `you request`,
                    past: `you requested`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
                return {
                    neutral: `you request`,
                    past: `you requested`,
                    amount: transaction?.amount
                };
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.EFT_DEBIT_DEPOSIT:
                return {
                    neutral: `you request`,
                    past: `you requested`,
                    amount: transaction?.amount
                };
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return {
                    neutral: `you send`,
                    past: `you sent`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.EPS_DEBIT_DEPOSIT:
                return {
                    neutral: `you request`,
                    past: `you requested`,
                    amount: transaction?.amount
                };
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return {
                    neutral: `you pay`,
                    past: `you payed`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD:
                return {
                    neutral: `you pay`,
                    past: `you payed`,
                    amount: transaction?.amount + transaction.fee
                };
            case EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM:
                return {
                    neutral: `you redeem`,
                    past: `you redeemed`,
                    amount: transaction?.amount
                };
            case EFundsOperation.GIFT_CARD_CREDIT_RESERVE:
                return {
                    neutral: `you reserve`,
                    past: `you reserved`,
                    amount: transaction?.amount
                };

            case EFundsOperation.GIFT_CARD_DEBIT_RESERVE:
                return {
                    neutral: `release`,
                    past: `released`,
                    amount: transaction?.amount
                };
            default:
                return undefined;
        }
    }, [transaction, getRecipientName]);

    const getRecipientString = useMemo(() => {
        const internalAction = getInternalTransferAction(
            transaction.method,
            transaction.action,
            transaction.type
        );
        switch (internalAction) {
            case EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT:
                return {
                    neutral: `you load`,
                    past: `you loaded`,
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_CREDIT_REQUEST:
                return {
                    neutral: `${getRecipientName} request`,
                    past: `${getRecipientName} requested`,
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_DEBIT_REQUEST:
                return {
                    neutral: `you receive`,
                    past: `you received`,
                    amount: transaction?.amount
                };
            case EFundsOperation.WALLET_CREDIT_TRANSFER:
                return {
                    neutral: `${getRecipientName} receive`,
                    past: `${getRecipientName} received`,
                    amount: transaction?.amount
                };
            case EFundsOperation.EFT_CREDIT_WITHDRAWAL:
                return {
                    neutral: `${getRecipientName} receive`,
                    past: `${getRecipientName} received`,
                    amount: transaction?.amount
                };
            // case EFundsOperation.EFT_DEBIT_DEPOSIT:
            //     return {
            //         neutral: `${getRecipientName} send`,
            //         past: `${getRecipientName} sent`,
            //         amount: transaction?.amount + transaction.fee
            //     };
            case EFundsOperation.E_TRANSFER_CREDIT_TRANSFER:
                return {
                    neutral: `${getRecipientName} receive`,
                    past: `${getRecipientName} received`,
                    amount: transaction?.amount
                };
            // case EFundsOperation.E_TRANSFER_DEBIT_REQUEST:
            //     return {
            //         neutral: `${getRecipientName} send`,
            //         past: `${getRecipientName} sent`,
            //         amount: transaction?.amount + transaction.fee
            //     };
            // case EFundsOperation.EPS_DEBIT_DEPOSIT:
            //     return {
            //         neutral: `${getRecipientName} send`,
            //         past: `${getRecipientName} sent`,
            //         amount: transaction?.amount + transaction.fee
            //     };
            case EFundsOperation.PAY_BILL_CREDIT_PAYMENT:
                return {
                    neutral: `${getRecipientName} receive`,
                    past: `${getRecipientName} received`,
                    amount: transaction?.amount
                };
            default:
                return undefined;
        }
    }, [transaction, getRecipientName]);

    const canBeRefunded = useMemo(() => {
        const methods = [ETransactionMethod.WALLET];
        const actions = [ETransactionAction.TRANSFER, ETransactionAction.REQUEST];
        return (
            transaction?.status === ETransactionStatus.APPROVED &&
            methods.includes(transaction?.method) &&
            actions.includes(transaction?.action) &&
            transaction?.type === ETransactionType.CREDIT
        );
    }, [transaction]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <div className={styles.user}>
                    <div className={styles.user_avatar}>{getAvatar}</div>
                    <p className={styles.user_name}>{getRecipientName}</p>
                    <p className={styles.user_email}>{getEmail}</p>
                    {transaction?.method !== ETransactionMethod.PAY_BILL && (
                        <p className={styles.method}>
                            {transaction?.method &&
                                enumRecipientTranslate[transaction.method][
                                    i18n.language as 'en' | 'fr'
                                ]}
                        </p>
                    )}
                    {transaction.status !== ETransactionStatus.ERROR &&
                        transaction.method === ETransactionMethod.WALLET &&
                        (transaction.action === ETransactionAction.REQUEST ||
                            transaction.action === ETransactionAction.TRANSFER) && (
                            <Button
                                size="small"
                                variant="text-red"
                                className={styles.block_user}
                                onClick={() => handleBlockContact(transaction.id)}>
                                {submitLocale('block')} {getRecipientName.split(' ')?.[0]}
                            </Button>
                        )}
                    <div className={styles.divider}>
                        <Typography variant="body4">
                            <Icon
                                name={
                                    transaction?.type === ETransactionType.CREDIT
                                        ? 'arrow_up'
                                        : 'arrow_down'
                                }
                            />
                            {getTransactionName}
                        </Typography>
                    </div>
                    <div className={styles.payment_id}>
                        <div className={styles.id}>
                            <p>Payment ID:</p>
                            <p>{transaction.id}</p>
                            <button onClick={() => copyStringToClipboard(transaction.id)}>
                                <Icon name="copy" width={18} height={18} />
                            </button>
                        </div>
                    </div>
                    <Status variant={getTransactionStatus(transaction?.status)}>
                        {transaction?.status &&
                            ETransactionStatusTranslate[transaction.status][
                                i18n.language as 'en' | 'fr'
                            ]}
                    </Status>
                </div>
                <div className={styles.details}>
                    <div className={styles.row}>
                        <p>Date and time</p>
                        <p>{formatDate(transaction.created_at, 'DD MMMM YYYY, HH:mm')}</p>
                    </div>
                    {getSenderString && (
                        <div className={styles.row}>
                            <p>
                                Amount{' '}
                                {transaction.status !== ETransactionStatus.APPROVED
                                    ? getSenderString?.neutral
                                    : getSenderString?.past}
                            </p>
                            <p className={styles.row_value}>
                                <b>
                                    {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                    {prettifyAmount(
                                        getSenderString?.amount?.toFixed(2) || '0.00'
                                    )}{' '}
                                    {transaction?.currency || 'CAD'}
                                </b>
                            </p>
                        </div>
                    )}
                    {fullInfoStatuses.includes(transaction?.status) && transaction?.fee !== 0 && (
                        <div className={styles.row}>
                            <p>Total fees (included)</p>
                            <p className={styles.row_value}>${transaction?.fee} CAD</p>
                        </div>
                    )}
                    {fullInfoStatuses.includes(transaction?.status) && getRecipientString && (
                        <div className={styles.row}>
                            <p>
                                Amount{' '}
                                {transaction.status !== ETransactionStatus.APPROVED
                                    ? getRecipientString?.neutral
                                    : getRecipientString?.past}
                            </p>
                            <p className={styles.row_value}>
                                <b>
                                    {' '}
                                    {currencies[transaction?.currency as 'CAD']?.symbol || '$'}
                                    {prettifyAmount(
                                        getRecipientString?.amount?.toFixed(2) || '0.00'
                                    )}{' '}
                                    {transaction?.currency || 'CAD'}
                                </b>
                            </p>
                        </div>
                    )}
                    {/*//@ts-ignore*/}
                    {!isNotMessage && transaction?.details?.message && (
                        <div className={styles.row}>
                            <p>
                                {transaction.method === ETransactionMethod.GIFT_CARD
                                    ? 'Reference'
                                    : 'Message'}
                            </p>
                            <p className={styles.row_value}>
                                {/*//@ts-ignore*/}
                                {transaction?.details?.message || '-'}
                            </p>
                        </div>
                    )}
                    {fullInfoStatuses.includes(transaction?.status) &&
                        transaction?.points !== 0 && (
                            <div className={styles.row}>
                                <p>
                                    Points you{' '}
                                    {transaction?.status === ETransactionStatus.APPROVED
                                        ? 'earned'
                                        : 'earn'}{' '}
                                    <Info
                                        variant="top"
                                        content={`Points you ${
                                            transaction?.status === ETransactionStatus.APPROVED
                                                ? 'earned'
                                                : 'will earn'
                                        } for transaction with your current level`}
                                    />{' '}
                                </p>
                                <p className={styles.row_value}>
                                    {transaction?.points?.toFixed(2)}
                                </p>
                            </div>
                        )}
                    {transaction?.details?.etransfer_reference_number &&
                        transaction.method === ETransactionMethod.E_TRANSFER &&
                        !(transaction.action === ETransactionAction.CARD_DEPOSIT) && (
                            <div className={styles.row}>
                                <p>{t('reference')}</p>
                                <p className={styles.row_value}>
                                    {transaction?.details?.etransfer_reference_number}
                                </p>
                            </div>
                        )}
                    {transaction.method === ETransactionMethod.GIFT_CARD &&
                        (transaction.details.gift_card_transaction_fee?.length || 0) > 1 && (
                            <div className={styles.dropdown_row}>
                                <p onClick={() => setIsListOpen(!isListOpen)}>
                                    Gift cards included <Icon name="nav_down" />
                                </p>
                                <div
                                    className={`${styles.list} ${isListOpen ? styles.isOpen : ''}`}>
                                    {transaction.details.gift_card_transaction_fee?.map(
                                        (item, i) => (
                                            <Typography variant="body4" key={i}>
                                                {item.gift_card_serial_number.toString()} - $
                                                {prettifyAmount(item.amount.toFixed(2))} CAD
                                            </Typography>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                </div>
            </div>
            <div className={styles.actions_wrapper}>
                <div className={styles.actions}>
                    <Button
                        variant="outlined-red"
                        size="medium"
                        onClick={() => {
                            handleOpenSubmitRequestModal(transaction?.id);
                        }}>
                        {submitLocale('report')}
                    </Button>
                    {canBeRefunded && (
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            onClick={() => {
                                handleOpenSubmitRequestModal(transaction?.id, true);
                            }}>
                            {submitLocale('refund')}
                        </Button>
                    )}
                    {isDebitRequest &&
                        isInternalTransactionRequest &&
                        transaction?.status === ETransactionStatus.PENDING && (
                            <Button
                                variant="red"
                                size="medium"
                                onClick={handleCancelTransaction}
                                isLoading={isCancelLoading}>
                                {submitLocale('cancel')}
                            </Button>
                        )}
                    {isCreditRequest &&
                        isInternalTransactionRequest &&
                        transaction?.status === ETransactionStatus.PENDING && (
                            <Button
                                variant="red"
                                size="medium"
                                onClick={handleDeclineTransaction}
                                isLoading={isDeclineLoading}>
                                {submitLocale('decline')}
                            </Button>
                        )}
                    {isCreditRequest &&
                        isInternalTransactionRequest &&
                        transaction?.status === ETransactionStatus.PENDING && (
                            <Button
                                variant="green"
                                size="medium"
                                onClick={handleApproveTransaction}
                                isLoading={isApproveLoading}>
                                {submitLocale('approve')}
                            </Button>
                        )}
                </div>
                {isDebitRequest &&
                    isInternalTransactionRequest &&
                    transaction?.status === ETransactionStatus.PENDING && (
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            disabled={reminderTimer !== 0}
                            onClick={handleSendReminder}
                            isLoading={isResendLoading}>
                            {submitLocale('send_reminder')}{' '}
                            {reminderTimer !== 0 && `(${reminderTimer}s)`}
                        </Button>
                    )}
            </div>
        </div>
    );
};

export default ViewTransactionModal;
